import { useQuery } from '@tanstack/react-query';
import { KeyRound, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDate, relativeTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

export interface SysAccessRow {
  id: string;
  email: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode: string | null;
  jobTitle: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  sysRoles: Array<{ code: string; name: string; nameAr: string | null }>;
  accessSince: string | null;
  lastSysSignInAt?: string | null;
}

/**
 * Who can use sys, and as what.
 *
 * Nobody is created here: an employee is created in the admin, and a sys admin
 * then lets them in from this page by giving them sys roles. Their admin roles
 * are never touched from here, and sys roles are never given out in the admin.
 */
export function AccessPage() {
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sys-access', q],
    queryFn: () => api.get<SysAccessRow[]>('/sys-access', { q: q.trim() || undefined }),
  });

  return (
    <>
      <PageHeader
        title={t('access.title')}
        subtitle={t('access.note')}
        count={data?.length}
        action={
          <Button variant="primary" icon={<UserPlus className="h-4 w-4" />} onClick={() => navigate('/access/new')}>
            {t('access.grant')}
          </Button>
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="p-4">
          <Input
            className="max-w-sm"
            placeholder={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          <EmptyState icon={<KeyRound className="h-8 w-8" />} title={t('access.empty')} hint="" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('access.employee')}</Th>
                <Th>{t('access.roles')}</Th>
                <Th>{t('access.since')}</Th>
                <Th>{t('access.lastSignIn')}</Th>
                <Th align="end">{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((person) => (
                <Tr key={person.id}>
                  <Td>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[var(--text-primary)]">{localized(person)}</span>
                      {person.id === user?.id && <Badge tone="info">{t('common.you')}</Badge>}
                      {!person.isActive && <Badge tone="warning">{t('access.disabled')}</Badge>}
                    </span>
                    <span className="block text-caption text-muted" dir="ltr">
                      {person.email}
                    </span>
                  </Td>
                  <Td>
                    <span className="flex flex-wrap gap-1.5">
                      {person.sysRoles.map((role) => (
                        <Badge key={role.code} tone={role.code === 'SYS_ADMIN' ? 'brand' : 'neutral'}>
                          {locale === 'ar' ? role.nameAr ?? role.name : role.name}
                        </Badge>
                      ))}
                    </span>
                  </Td>
                  <Td className="tabular text-muted">{person.accessSince ? formatDate(person.accessSince, locale) : '—'}</Td>
                  <Td className="text-muted">
                    {person.lastSysSignInAt ? relativeTime(person.lastSysSignInAt, locale) : t('common.never')}
                  </Td>
                  <Td align="end">
                    <span className="inline-flex gap-2">
                      <Button size="sm" onClick={() => navigate(`/access/${person.id}/edit`)}>
                        {t('common.edit')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/access/${person.id}/revoke`)}>
                        {t('access.revoke')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
